// src/scenes/.../ApiOnboarding.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Divider, Alert, CircularProgress,
  IconButton, Tooltip, Snackbar, Link, useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GridCheckCircleIcon } from '@mui/x-data-grid';
import { tokens } from '../../../theme';

import {
  useEnableApisProductMutation,
  useCreateOrgApiKeyMutation
} from '../../../features/organizations/organizationsApiSlice';

const ApiOnboarding = ({ organization, onComplete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const orgId = organization?.organization_id;

  const [enableApisProduct, { isLoading: enabling }] = useEnableApisProductMutation();
  const [createOrgApiKey,   { isLoading: creating }] = useCreateOrgApiKeyMutation();

  const [phase, setPhase] = useState('idle'); // idle -> enabling -> enabled -> keyReady
  const [apiKey, setApiKey] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '' });
  const apiHome = useMemo(() => `/console/organization/${orgId}?tab=APIs`, [orgId]);

  const notify = (msg) => setSnack({ open: true, msg });

  async function enableProduct() {
    setPhase('enabling');
    try {
      await enableApisProduct({ id: orgId }).unwrap();
      setPhase('enabled');
      notify('API product enabled for this organization.');
    } catch (e) {
      setPhase('idle');
      notify(e?.data?.message || 'Failed to enable API product.');
    }
  }

  async function generateApiKey() {
    try {
      const resp = await createOrgApiKey({ id: orgId }).unwrap();
      if (resp.api_key) {
        setApiKey(resp.api_key);
        setPhase('keyReady');
        notify(resp.demo
          ? 'Demo key generated (configure API_USAGE_PLAN_ID for real keys).'
          : 'API key created. Copy and store it securely (shown once).');
      } else {
        notify('No api_key returned.');
      }
    } catch (e) {
      notify(e?.data?.message || 'Failed to create API key.');
    }
  }

  const copyKey = async () => {
    if (!apiKey) return;
    try { await navigator.clipboard.writeText(apiKey); notify('Copied API key.'); }
    catch { notify('Could not copy—select & copy manually.'); }
  };

  const handleDone = () => onComplete?.();

  return (
    <Paper elevation={6} sx={{
      p: 3, borderRadius: 2, minWidth: { xs: 360, sm: 560 }, maxWidth: 720,
      background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[800]})`,
    }}>
      <Typography variant="h5" gutterBottom>Enable 5×5 APIs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Onboard <strong>{organization?.name}</strong>, create an org-level API key, then go to the API dashboard.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Step 1 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>1) Enable API product</Typography>
        <Button variant="contained" onClick={enableProduct} disabled={enabling || phase !== 'idle'}>
          {enabling || phase === 'enabling' ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {phase === 'enabled' || phase === 'keyReady' ? 'Enabled' : 'Enable'}
        </Button>
      </Box>
      {phase !== 'idle' && (
        <Alert icon={<GridCheckCircleIcon />} severity={(phase === 'enabled' || phase === 'keyReady') ? 'success' : 'info'} sx={{ mb: 2 }}>
          {phase === 'enabled' || phase === 'keyReady' ? 'Organization is enabled for 5×5 APIs.' : 'Enabling…'}
        </Alert>
      )}

      {/* Step 2 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>2) Create org API key</Typography>
        <Button variant="outlined" onClick={generateApiKey} disabled={creating || (phase !== 'enabled' && phase !== 'keyReady')}>
          {creating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {apiKey ? 'Recreate' : 'Create key'}
        </Button>
      </Box>

      {apiKey && (
        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ overflowX: 'auto', fontFamily: 'monospace', fontSize: 14 }}>
            {apiKey}
          </Box>
          <Tooltip title="Copy key">
            <IconButton onClick={copyKey} size="small" sx={{ ml: 1 }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      {/* Step 3 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>3) Visit API dashboard</Typography>
        <Button variant="contained" color="secondary" component={Link} href={apiHome} underline="none" disabled={phase !== 'keyReady'}>
          Open API Home
        </Button>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={handleDone}>Close</Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack({ open: false, msg: '' })} message={snack.msg} />
    </Paper>
  );
};

export default ApiOnboarding;
